import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Sparkles, CheckCircle2, Ticket, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import api from "../../lib/api";

export default function SignupDialog({ open, onOpenChange, initialMode = "signup" }) {
  const navigate = useNavigate();
  const { signUp, signIn, signInWithGoogle, resetPassword, user, refreshSubscription } = useAuth();
  const [mode, setMode] = useState("signup"); // "signup", "signin", "forgot"
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", role: "Founder" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [couponSuccess, setCouponSuccess] = useState(false);

  const handleCouponActivation = async (e) => {
    if (e) e.preventDefault();
    const code = couponInput.trim().toUpperCase();
    if (!code) return;

    if (code !== "STUDLYF499" && code !== "PRO30D_ACTIVE_REUSE" && code !== "PRO30D_ACTIVE") {
      toast.error("Invalid coupon code.");
      return;
    }

    setValidatingCoupon(true);
    try {
      if (user) {
        const res = await api.post("/subscription/activate-coupon", { coupon_code: code });
        if (res.data.success) {
          toast.success("🎉 STUDLYF Pro activated successfully! Your Pro access is valid for 30 days.");
          if (refreshSubscription) await refreshSubscription();
          setShowCouponModal(false);
        }
      } else {
        localStorage.setItem("studlyf_pending_coupon", code);
        toast.success("🎉 STUDLYF Pro activated successfully! Your Pro access is valid for 30 days.");
        setShowCouponModal(false);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Invalid coupon code.";
      if (detail === "You have already redeemed this coupon.") {
        toast.error("You have already redeemed this coupon.");
      } else {
        toast.error(detail);
      }
    } finally {
      setValidatingCoupon(false);
    }
  };

  useEffect(() => {
    if (open) {
      setMode(initialMode === "login" ? "signin" : "signup");
      setDone(false);
      setAcceptedTerms(false);
      setShowTermsError(false);
    }
  }, [open, initialMode]);

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setAcceptedTerms(false);
    setShowTermsError(false);
  };

  const handleGoogleSignIn = async (e) => {
    e.preventDefault();
    if (mode !== "forgot" && !acceptedTerms) {
      setShowTermsError(true);
      toast.error("You must accept the Privacy Policy and Terms & Conditions before continuing.");
      return;
    }
    try {
      await signInWithGoogle();
    } catch (err) {
      console.error("Google sign in failed:", err);
      toast.error(err?.message || "Failed to start Google sign in. Please try again.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode !== "forgot" && !acceptedTerms) {
      setShowTermsError(true);
      return;
    }
    setLoading(true);
    try {
      if (mode === "signup") {
        const data = await signUp(form.email, form.password, {
          name: form.name,
          company: form.company,
          role: form.role,
        });
        if (data?.session || data?.user) {
          toast.success("Signup successful! Welcome to your workspace.");
          onOpenChange(false);
          navigate("/workspace");
        } else {
          setDone(true);
          toast.success("Signup successful! Please check your email to confirm your account.");
        }
      } else if (mode === "signin") {
        await signIn(form.email, form.password);
        toast.success("Welcome back!");
        onOpenChange(false);
        navigate("/workspace");
      } else if (mode === "forgot") {

        await resetPassword(form.email);
        toast.success("Password reset instructions sent to your email!");
        handleModeChange("signin");
      }
    } catch (err) {
      const msg = err?.message || "Authentication failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setDone(false);
      setForm({ name: "", email: "", password: "", company: "", role: "Founder" });
      setAcceptedTerms(false);
      setShowTermsError(false);
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(true); }}>
      <DialogContent className="sm:max-w-md rounded-[24px] border-gray-100 p-0 overflow-hidden" data-testid="signup-dialog">
        {!done ? (
          <>
            <div className="relative px-7 pt-7 pb-2">
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full blob"
                   style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 70%)", opacity: 0.25 }} />
              <DialogHeader className="text-left relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-xs font-semibold w-fit">
                  <Sparkles className="w-3.5 h-3.5 text-[#7C3AED]" /> 
                  {mode === "signup" ? "Get Started" : mode === "signin" ? "Welcome Back" : "Reset Password"}
                </div>
                <DialogTitle className="font-display text-2xl md:text-3xl font-semibold tracking-tight mt-3">
                  {mode === "signup" ? "Start building your startup with AI." : mode === "signin" ? "Sign in to your workspace." : "Recover your account."}
                </DialogTitle>
                <DialogDescription>
                  {mode === "signup" 
                    ? "Create an account to instantly open your workspace — or sign in to continue."
                    : mode === "signin"
                    ? "Welcome back! Enter your details or continue with Google to access your dashboard."
                    : "Enter your email address and we'll send you instructions to reset your password."}
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="px-7 pb-2">
              <button
                onClick={handleGoogleSignIn}
                className="w-full rounded-full py-3 text-sm font-semibold bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm flex items-center justify-center gap-3 transition"
                data-testid="signup-google"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.55c2.08-1.92 3.29-4.74 3.29-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.55-2.76c-.98.66-2.23 1.06-3.73 1.06-2.87 0-5.3-1.94-6.17-4.54H2.18v2.85A11 11 0 0 0 12 23z"/>
                  <path fill="#FBBC05" d="M5.83 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.33-2.1V7.05H2.18A11 11 0 0 0 1 12c0 1.77.42 3.44 1.18 4.95l3.65-2.85z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.65l3.15-3.15C17.45 2.1 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05L5.83 9.9C6.7 7.3 9.13 5.38 12 5.38z"/>
                </svg>
                Continue with Google
              </button>
              <div className="my-4 flex items-center gap-3 text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                <span className="flex-1 h-px bg-gray-100" /> or use your email <span className="flex-1 h-px bg-gray-100" />
              </div>
            </div>

            <form onSubmit={submit} className="px-7 pb-7 space-y-3" data-testid="signup-form">
              {mode === "signup" && (
                <input
                  type="text" placeholder="Full name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 text-sm outline-none animate-fadeIn"
                  data-testid="signup-name"
                />
              )}
              <input
                type="email" placeholder="Work email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 text-sm outline-none"
                data-testid="signup-email"
              />
              {mode !== "forgot" && (
                <input
                  type="password" placeholder="Password (min 6 chars)" required value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 text-sm outline-none"
                  data-testid="signup-password"
                  minLength={6}
                />
              )}
              {mode === "signup" && (
                <>
                  <input
                    type="text" placeholder="Company / Project (optional)" value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 text-sm outline-none animate-fadeIn"
                    data-testid="signup-company"
                  />
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 text-sm outline-none bg-white animate-fadeIn"
                    data-testid="signup-role"
                  >
                    {["Founder", "Student", "Mentor", "Agency", "Incubator", "Investor"].map((r) => <option key={r}>{r}</option>)}
                  </select>
                </>
              )}

              {mode !== "forgot" && (
                <div className="flex flex-col gap-1 text-left mt-2" data-testid="terms-container">
                  <div className="flex items-start gap-3 mt-1 select-none">
                    <div className="flex items-center h-5">
                      <input
                        id="terms-checkbox"
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => {
                          setAcceptedTerms(e.target.checked);
                          if (e.target.checked) setShowTermsError(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            setAcceptedTerms((prev) => !prev);
                            if (!acceptedTerms) setShowTermsError(false);
                          }
                        }}
                        className={`w-4 h-4 rounded border text-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 transition duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED] ${
                          showTermsError
                            ? "border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500/15 dark:border-red-500 dark:bg-red-950/20"
                            : "border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 focus:border-[#7C3AED]"
                        }`}
                        style={{ accentColor: "#7C3AED" }}
                        data-testid="terms-checkbox"
                      />
                    </div>
                    <label
                      htmlFor="terms-checkbox"
                      className={`text-xs text-gray-500 dark:text-gray-400 cursor-pointer font-medium leading-normal ${
                        showTermsError ? "text-red-500 dark:text-red-400 font-semibold" : ""
                      }`}
                    >
                      I have read and agree to the{" "}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#7C3AED] hover:underline font-semibold"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </a>{" "}
                      and{" "}
                      <a
                        href="/terms-and-conditions"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#7C3AED] hover:underline font-semibold"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms & Conditions
                      </a>
                      .
                    </label>
                  </div>
                  {showTermsError && (
                    <p className="text-red-500 dark:text-red-400 text-[11px] font-semibold mt-0.5" data-testid="terms-error">
                      You must accept the Privacy Policy and Terms & Conditions before continuing.
                    </p>
                  )}
                </div>
              )}

              <button
                type="submit" disabled={loading}
                className="glow-button w-full rounded-full py-3.5 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70 mt-2"
                data-testid="signup-submit"
              >
                {loading 
                  ? "Processing..." 
                  : mode === "signup" 
                  ? "Sign Up" 
                  : mode === "signin"
                  ? "Sign In"
                  : "Send Reset Link"}
              </button>

              {/* Redeem Pro Coupon Button on Login Mode */}
              {mode === "signin" && (
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setCouponInput("");
                    setCouponSuccess(false);
                    setShowCouponModal(true);
                  }}
                  className="w-full mt-2.5 rounded-full py-3 text-xs font-bold border border-[#7C3AED]/40 hover:border-[#7C3AED] text-[#7C3AED] bg-[#7C3AED]/5 hover:bg-[#7C3AED]/10 dark:bg-[#7C3AED]/10 dark:hover:bg-[#7C3AED]/20 transition-all flex items-center justify-center gap-2 shadow-2xs"
                  data-testid="login-redeem-coupon-btn"
                >
                  <Ticket className="w-4 h-4 text-[#7C3AED]" /> Redeem Pro Coupon
                </motion.button>
              )}

              <div className="flex flex-col gap-2 pt-2 text-center text-xs text-gray-500">
                {mode === "signin" && (
                  <>
                    <button type="button" onClick={() => handleModeChange("signup")} className="hover:text-gray-900 transition">
                      Don&apos;t have an account? <span className="text-[#7C3AED] font-semibold">Sign Up</span>
                    </button>
                    <button type="button" onClick={() => handleModeChange("forgot")} className="hover:text-gray-900 transition text-[11px] text-gray-400">
                      Forgot your password?
                    </button>
                  </>
                )}
                {mode === "signup" && (
                  <button type="button" onClick={() => handleModeChange("signin")} className="hover:text-gray-900 transition">
                    Already have an account? <span className="text-[#7C3AED] font-semibold">Sign In</span>
                  </button>
                )}
                {mode === "forgot" && (
                  <button type="button" onClick={() => handleModeChange("signin")} className="hover:text-gray-900 transition">
                    Back to <span className="text-[#7C3AED] font-semibold">Sign In</span>
                  </button>
                )}
              </div>
            </form>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="px-7 py-12 text-center"
            data-testid="signup-success"
          >
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-[#22C55E] to-[#6366F1] flex items-center justify-center text-white">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h3 className="mt-5 font-display text-2xl font-semibold">Verify your email!</h3>
            <p className="mt-2 text-gray-600 text-sm">We sent a confirmation link to {form.email}. Click the link to complete your setup.</p>
            <div className="mt-6 flex items-center justify-center gap-2">
              <button onClick={handleClose} className="rounded-full px-5 py-2.5 text-sm font-semibold border border-gray-200" data-testid="signup-success-close">
                Close
              </button>
            </div>
          </motion.div>
        )}
      </DialogContent>

      {/* ACTIVATE STUDLYF PRO COUPON MODAL */}
      <Dialog open={showCouponModal} onOpenChange={setShowCouponModal}>
        <DialogContent className="sm:max-w-md rounded-[28px] border-gray-150 p-0 overflow-hidden bg-white/95 backdrop-blur-xl dark:bg-gray-900/95 shadow-2xl" data-testid="coupon-modal">
          <div className="relative p-6 bg-gradient-to-br from-[#7C3AED]/10 via-[#EC4899]/5 to-transparent border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2.5 text-[#7C3AED] font-extrabold text-xs tracking-wider uppercase mb-1">
              <Sparkles className="w-4 h-4 fill-current" /> STUDLYF Pro Access
            </div>
            <h3 className="font-display text-2xl font-black text-gray-900 dark:text-white tracking-tight">
              Activate STUDLYF Pro
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed font-medium">
              Enter your Pro access coupon code to unlock STUDLYF Pro for 30 days.
            </p>
          </div>

          <form onSubmit={handleCouponActivation} className="p-6 space-y-4">
            <div className="space-y-1.5 text-left">
              <label className="block text-xs font-extrabold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Coupon Code
              </label>
              <input
                type="text"
                placeholder="Enter your coupon code"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase().trim())}
                disabled={validatingCoupon}
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 text-sm font-mono uppercase tracking-wider outline-none text-gray-900 dark:text-white"
                data-testid="coupon-input"
                autoFocus
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-3">
              <button
                type="button"
                onClick={() => setShowCouponModal(false)}
                disabled={validatingCoupon}
                className="px-5 py-2.5 rounded-full text-xs font-bold border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                data-testid="coupon-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={validatingCoupon || !couponInput}
                className="px-6 py-2.5 rounded-full text-xs font-bold bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white hover:opacity-90 transition shadow-md flex items-center gap-1.5 disabled:opacity-50"
                data-testid="coupon-activate-btn"
              >
                {validatingCoupon ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Zap className="w-3.5 h-3.5 fill-current" />
                )}
                Activate
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
